import { Component, Input, forwardRef } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

@Component({
  selector: 'lib-color-picker-field',
  standalone: true,
  template: `
    <div class="color-picker-field">
      <input
        class="color-picker-field__text"
        type="text"
        [value]="value"
        [attr.placeholder]="placeholder"
        [disabled]="disabled"
        [attr.aria-label]="ariaLabel || placeholder || 'Hex color'"
        (input)="onTextInput($event)"
        (blur)="onBlur()"
      />

      <input
        class="color-picker-field__picker"
        type="color"
        [value]="pickerValue"
        [disabled]="disabled"
        [attr.aria-label]="pickerAriaLabel"
        (input)="onPickerInput($event)"
        (blur)="onBlur()"
      />
    </div>
  `,
  styles: [`
    :host {
      display: block;
    }

    .color-picker-field {
      display: grid;
      grid-template-columns: minmax(0, 1fr) auto;
      gap: 10px;
      align-items: center;
    }

    .color-picker-field__text {
      width: 100%;
      min-width: 0;
      border: 1px solid #cbd5e1;
      border-radius: 10px;
      padding: 10px 12px;
      color: #0f172a;
      background: #ffffff;
      box-sizing: border-box;
      font: inherit;
    }

    .color-picker-field__text:focus,
    .color-picker-field__picker:focus-visible {
      outline: none;
      border-color: #2563eb;
      box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.12);
    }

    .color-picker-field__picker {
      width: 42px;
      height: 42px;
      padding: 0;
      border-radius: 14px;
      border: 1px solid #cbd5e1;
      background: #ffffff;
      cursor: pointer;
      box-sizing: border-box;
      box-shadow:
        inset 0 1px 0 rgba(255, 255, 255, 0.7),
        0 10px 20px rgba(15, 23, 42, 0.08);
      overflow: hidden;
      transition: transform 0.15s ease, box-shadow 0.15s ease;
      -webkit-appearance: none;
      appearance: none;
    }

    .color-picker-field__picker:disabled {
      cursor: default;
      opacity: 0.6;
    }

    .color-picker-field__picker::-webkit-color-swatch-wrapper {
      padding: 4px;
    }

    .color-picker-field__picker::-webkit-color-swatch {
      border-radius: 14px;
      border: none;
    }

    .color-picker-field__picker::-moz-color-swatch {
      border: none;
      border-radius: 10px;
    }

    .color-picker-field__picker:hover:not(:disabled) {
      transform: translateY(-1px);
      box-shadow:
        inset 0 1px 0 rgba(255, 255, 255, 0.7),
        0 14px 24px rgba(15, 23, 42, 0.12);
    }

    @media (max-width: 640px) {
      .color-picker-field {
        gap: 8px;
      }

      .color-picker-field__picker {
        width: 38px;
        height: 38px;
      }
    }
  `],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => ColorPickerFieldComponent),
      multi: true,
    },
  ],
})
export class ColorPickerFieldComponent implements ControlValueAccessor {
  @Input() public placeholder = '#2563eb';
  @Input() public fallbackColor = '#000000';
  @Input() public ariaLabel = '';
  @Input() public pickerAriaLabel = 'Pick color';

  protected value = '';
  protected disabled = false;

  private onChange: (value: string) => void = () => {};
  private onTouched: () => void = () => {};

  protected get pickerValue(): string {
    const normalizedCurrent = this.normalizeHexColor(this.value);
    if (normalizedCurrent) {
      return normalizedCurrent;
    }

    return this.normalizeHexColor(this.fallbackColor) ?? '#000000';
  }

  public writeValue(value: string | null): void {
    this.value = typeof value === 'string' ? value : '';
  }

  public registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  public registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  public setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  protected onTextInput(event: Event): void {
    const nextValue = (event.target as HTMLInputElement).value;
    this.value = nextValue;
    this.onChange(nextValue);
  }

  protected onPickerInput(event: Event): void {
    const nextValue = this.normalizeHexColor((event.target as HTMLInputElement).value) ?? this.pickerValue;
    this.value = nextValue;
    this.onChange(nextValue);
  }

  protected onBlur(): void {
    this.onTouched();
  }

  private normalizeHexColor(value: string | null | undefined): string | null {
    const normalized = value?.trim().toLowerCase() ?? '';

    if (/^#[0-9a-f]{6}$/.test(normalized)) {
      return normalized;
    }

    if (/^#[0-9a-f]{3}$/.test(normalized)) {
      return `#${normalized[1]}${normalized[1]}${normalized[2]}${normalized[2]}${normalized[3]}${normalized[3]}`;
    }

    return null;
  }
}
